// Core packages
import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from "@angular/router";

// Third party packages
import { CookieService } from "ngx-cookie-service";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";

/**
 * Route guard demanding EVERY permission listed in `data.permissions`.
 *
 * It exists beside `RoleGuard`, which checks the same key with an OR: some back-end
 * routes (the data types catalogue is one) are protected by `hasPerms([a, b])`, an AND,
 * and guarding them with the OR guard would let a user reach a page that answers 403 on
 * every single call.
 *
 * Same `data.permissions` key as RoleGuard on purpose, so that swapping one guard for
 * the other on a route is a one word change.
 *
 *   {
 *     path: 'data-types',
 *     component: DataTypesComponent,
 *     canActivate: [AllPermissionsGuard],
 *     data: { permissions: ['canReadDocumentsList', 'canCreateSettings'] },
 *   }
 *
 * @since 3.0.0
 */
@Injectable({
  providedIn: "root",
})
export class AllPermissionsGuard implements CanActivate {
  private loggedUser: IUser;
  private loggedUserPermissions: string[];
  private lang: string;

  /**
   * Class constructor
   */
  constructor(
    private router: Router,
    private authService: AuthService,
    private cookieService: CookieService,
    private configService: ConfigService
  ) {}

  /**
   * Let the route through only when the user is logged in AND the role currently
   * selected owns every requested permission.
   *
   * @since 3.0.0
   *
   * @returns true when allowed; false (plus a redirect) otherwise
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    this.loggedUser = this.authService.loggedUser$.value;
    this.loggedUserPermissions = this.authService.loggedUserPermissions$.value;
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;

    // Re-check the session: an expired one goes back to the login page
    if (!this.loggedUser || !this.loggedUser._id) {
      this.router.navigate([this.lang, "auth", "login"], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }

    if (this.hasEveryPermission(route.data.permissions as string[])) {
      return true;
    }

    this.router.navigate([this.lang, "errors", "400"]);
    return false;
  }

  /**
   * True when the current role owns EVERY requested permission. A route declaring no
   * permission is open, exactly like in RoleGuard.
   *
   * @since 3.0.0
   */
  hasEveryPermission(requestedPermissions: string[]): boolean {
    if (!requestedPermissions || !requestedPermissions.length) {
      return true;
    }
    const owned = this.loggedUserPermissions
      ? this.loggedUserPermissions
      : [];
    return requestedPermissions.every((perm) => owned.indexOf(perm) >= 0);
  }
}
